import { useState, useMemo, useCallback } from 'react';
import { binaryToInt32, int32ToBinary, joinBytes, splitIntoBytes } from '../utils/binaryConversion';
import { DEFAULT_BYTE_2, DEFAULT_BYTE_3, PresetterMode } from '../utils/constants';

export interface CalculatorState {
  mode: PresetterMode;
  bytes: [string, string, string, string];
  cncDiameter: string;
  measuredDiameter: string;
}

export interface CalculationResult {
  originalOffset: number;
  error: number;
  newOffset: number;
  newBinary: string;
  newBytes: [string, string, string, string];
}

const INITIAL_BYTES: [string, string, string, string] = [
  '00000000',
  '00000000',
  '00000000',
  '00000000'
];

export function useOffsetCalculator() {
  const [mode, setMode] = useState<PresetterMode>('XPL');
  const [bytes, setBytes] = useState<[string, string, string, string]>(INITIAL_BYTES);
  const [cncDiameter, setCncDiameter] = useState('');
  const [measuredDiameter, setMeasuredDiameter] = useState('');

  const state: CalculatorState = {
    mode,
    bytes,
    cncDiameter,
    measuredDiameter
  };

  const result = useMemo<CalculationResult | null>(() => {
    const binary = joinBytes(bytes);
    const originalOffset = binaryToInt32(binary);

    const cnc = parseFloat(cncDiameter);
    const measured = parseFloat(measuredDiameter);

    if (isNaN(cnc) || isNaN(measured)) {
      return null;
    }

    const error = measured - cnc;
    // Truncate (toward zero) the result after multiplying error by 1000
    const newOffset = Math.trunc(originalOffset + (error * 1000));
    const newBinary = int32ToBinary(newOffset);
    const newBytes = splitIntoBytes(newBinary);

    return {
      originalOffset,
      error,
      newOffset,
      newBinary,
      newBytes
    };
  }, [bytes, cncDiameter, measuredDiameter]);

  const originalOffset = useMemo(() => {
    return binaryToInt32(joinBytes(bytes));
  }, [bytes]);

  const setBit = useCallback((byteIndex: number, bitIndex: number, value: '0' | '1') => {
    setBytes(prev => {
      const newBytes = [...prev] as [string, string, string, string];
      const byteArray = newBytes[byteIndex].split('');
      byteArray[bitIndex] = value;
      newBytes[byteIndex] = byteArray.join('');
      return newBytes;
    });
  }, []);

  const setByte = useCallback((byteIndex: number, value: string) => {
    if (value.length === 8 && /^[01]+$/.test(value)) {
      setBytes(prev => {
        const newBytes = [...prev] as [string, string, string, string];
        newBytes[byteIndex] = value;
        return newBytes;
      });
    }
  }, []);

  const prefillBytes23 = useCallback(() => {
    setBytes(prev => {
      const newBytes = [...prev] as [string, string, string, string];
      newBytes[2] = DEFAULT_BYTE_2;
      newBytes[3] = DEFAULT_BYTE_3;
      return newBytes;
    });
  }, []);

  const clearAll = useCallback(() => {
    setBytes(INITIAL_BYTES);
    setCncDiameter('');
    setMeasuredDiameter('');
  }, []);

  const loadFromHistory = useCallback((historyState: CalculatorState) => {
    setMode(historyState.mode);
    setBytes(historyState.bytes);
    setCncDiameter(historyState.cncDiameter);
    setMeasuredDiameter(historyState.measuredDiameter);
  }, []);

  return {
    mode,
    setMode,
    bytes,
    setBit,
    setByte,
    setBytes,
    cncDiameter,
    setCncDiameter,
    measuredDiameter,
    setMeasuredDiameter,
    originalOffset,
    result,
    state,
    prefillBytes23,
    clearAll,
    loadFromHistory
  };
}
